/**
 * operatorSession.ts — the ONE place any browser surface resolves the
 * signed-in operator's access token, and the one place it decides what to say
 * when there isn't one.
 *
 * ora-core is the SOURCE for this module. `teglo-dashboard` vendors a
 * byte-identical copy at `components/canonical/orbi/src/lib/operatorSession.ts`
 * for the Jarvis dock (`operatorActions.ts`). Land changes here first, then
 * carry the file across unchanged — same law as
 * `supabase/functions/_shared/spoken-spelling.ts`.
 *
 * WHY THIS EXISTS
 *
 * The dock's old `bearer()` returned the project ANON key whenever it failed
 * to find a session: no storage entry, an unparseable entry, an entry with no
 * access_token, or an expired one. All four collapsed into the same silent
 * downgrade. Anon lands at PostgREST as role `anon`, and the authenticated-only
 * RLS policies on the PII tables answer role `anon` with 200 OK and an empty
 * array — never an error. So a signed-out or expired operator saw "No billing
 * row found for this subscriber", which is a statement about the DATA. The
 * truth was a statement about the SESSION. That is the bug: a credential miss
 * must never be laundered into a data answer.
 *
 * Two rules follow, and this module exists to make them mechanical:
 *
 *   1. NO ANON FALLBACK on Authorization. If we cannot produce the operator's
 *      own token, we produce no headers at all and the caller must surface the
 *      auth failure. `apikey` still carries the anon key, because that header
 *      identifies the PROJECT, not the caller — that is the documented Supabase
 *      shape, not a degraded credential.
 *   2. SIGNED OUT AND EXPIRED ARE DIFFERENT ANSWERS. "Sign in" and "sign in
 *      again" send an operator to different places in their own head. Every
 *      miss is classified and named.
 *
 * WHERE THE TOKEN ACTUALLY LIVES
 *
 * No `createClient()` call in either app passes an `auth.storageKey` override,
 * so the supabase-js default applies. That default has changed across major
 * versions, and the dock's old scan only ever knew one of the shapes — which is
 * how it managed to miss a live session and hand back anon for a fully
 * signed-in operator. We therefore read every shape that is actually in the
 * wild rather than betting on one:
 *
 *   - the literal `supabase.auth.token` (supabase-js v1)
 *   - `sb-<project-ref>-auth-token` (supabase-js v2)
 *   - the same key chunked as `<key>.0`, `<key>.1`, … (large sessions)
 *   - a raw JSON value, or a `base64-`prefixed encoding of it (v2.6x+)
 *   - a session object holding the token at `access_token` (v2) or nested
 *     under `currentSession` (v1)
 *
 * Reading it is deliberately synchronous and dependency-free: the callers build
 * fetch headers inline, and vendoring into another repo must not drag a client
 * instance or an await with it.
 */

/** The subset of the Web Storage API this module needs. Injectable so the
 *  behaviour is testable under a node test environment, where there is no
 *  `localStorage` — and so a harness supplies a real fixture rather than
 *  silently inheriting anon, which is exactly what we are removing. */
export interface SessionStorageLike {
  readonly length: number;
  key(index: number): string | null;
  getItem(key: string): string | null;
}

/** Why we could not authenticate as the operator. Each value maps to its own
 *  operator-facing sentence; none of them may be answered with anon. */
export type OperatorAuthFailure =
  /** No Supabase session entry in storage at all. Nobody has signed in here. */
  | 'signed_out'
  /** A session is present but its access token is past its expiry. */
  | 'expired'
  /** A session entry exists but cannot be parsed, or carries no access token. */
  | 'unreadable'
  /** The surface was built without its Supabase anon key, so even the project
   *  `apikey` header cannot be formed. A deploy problem, not a login problem. */
  | 'missing_project_key';

export type OperatorSessionState = 'authenticated' | OperatorAuthFailure;

export interface OperatorSession {
  state: 'authenticated' | 'signed_out' | 'expired' | 'unreadable';
  /** The operator's own JWT. Non-null if and only if state is 'authenticated'. */
  accessToken: string | null;
  /** Expiry in epoch SECONDS when we could determine it, else null. */
  expiresAt: number | null;
  /** Which storage key the session was read from. Diagnostics only. */
  storageKey: string | null;
}

/** supabase-js v1's fixed storage key. */
export const LEGACY_SESSION_STORAGE_KEY = 'supabase.auth.token';
/** supabase-js v2's per-project key, optionally chunked with a `.N` suffix. */
export const SESSION_STORAGE_KEY_PATTERN = /^sb-.+-auth-token(\.\d+)?$/;

/** Clock leeway applied when judging expiry. A browser clock running fast would
 *  otherwise lock an operator out of a token the server still honours. We err
 *  toward calling the token live: if we are wrong, the server answers 401 and
 *  `isAuthFailureStatus` turns that into the same honest message. */
const EXPIRY_LEEWAY_MS = 10_000;

/* ── Reading the stored session ─────────────────────────────────────── */

function decodeBase64(value: string): string | null {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.length % 4 === 0
    ? normalized
    : normalized + '='.repeat(4 - (normalized.length % 4));
  const atobFn = (globalThis as { atob?: (s: string) => string }).atob;
  if (typeof atobFn !== 'function') return null;
  try {
    const binary = atobFn(padded);
    // atob yields latin1; decode the bytes as UTF-8 so a non-ASCII field
    // elsewhere in the session cannot break JSON.parse and cost us the token.
    const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

function parseStoredSession(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const json = trimmed.startsWith('base64-')
    ? decodeBase64(trimmed.slice('base64-'.length))
    : trimmed;
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Pull `exp` out of a JWT without verifying it. We are not making a trust
 *  decision here — the server does that — we are only deciding which honest
 *  sentence to show when the stored envelope carries no expiry of its own. */
function jwtExpiry(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  const json = decodeBase64(parts[1]);
  if (!json) return null;
  try {
    const claims = JSON.parse(json) as { exp?: unknown };
    return typeof claims.exp === 'number' ? claims.exp : null;
  } catch {
    return null;
  }
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

interface ExtractedSession {
  accessToken: string;
  expiresAt: number | null;
}

function extractSession(envelope: Record<string, unknown>): ExtractedSession | null {
  const nested = envelope.currentSession;
  const session = (nested && typeof nested === 'object')
    ? (nested as Record<string, unknown>)
    : envelope;
  const token = session.access_token;
  if (typeof token !== 'string' || !token) return null;
  // v2 puts `expires_at` (epoch seconds) on the session; v1 put `expiresAt` on
  // the envelope. Fall back to the token's own `exp` claim when neither is set.
  const expiresAt = readNumber(session.expires_at)
    ?? readNumber(envelope.expiresAt)
    ?? jwtExpiry(token);
  return { accessToken: token, expiresAt };
}

function isExpired(expiresAt: number | null, now: number): boolean {
  if (expiresAt === null) return false;
  return expiresAt * 1000 + EXPIRY_LEEWAY_MS <= now;
}

function defaultStorage(): SessionStorageLike | null {
  try {
    const candidate = (globalThis as { localStorage?: SessionStorageLike }).localStorage;
    return candidate && typeof candidate.getItem === 'function' ? candidate : null;
  } catch {
    // Storage access throws outright when the browser blocks it (third-party
    // context, hardened privacy mode). No session is readable; say so.
    return null;
  }
}

/** Group the storage keys we care about, joining any `.0`/`.1` chunks back into
 *  one value. Legacy key first: it is the one the vendored supabase-js in the
 *  dock actually writes, and the old scan's regex never matched it. */
function collectCandidates(storage: SessionStorageLike): Array<{ key: string; raw: string }> {
  const chunks = new Map<string, Map<number, string>>();
  const whole = new Map<string, string>();
  const order: string[] = [];

  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (!key) continue;
    if (key !== LEGACY_SESSION_STORAGE_KEY && !SESSION_STORAGE_KEY_PATTERN.test(key)) continue;
    const chunkMatch = key.match(/^(.*)\.(\d+)$/);
    const base = chunkMatch ? chunkMatch[1] : key;
    const value = storage.getItem(key);
    if (value === null) continue;
    if (!order.includes(base)) order.push(base);
    if (chunkMatch) {
      const parts = chunks.get(base) ?? new Map<number, string>();
      parts.set(Number(chunkMatch[2]), value);
      chunks.set(base, parts);
    } else {
      whole.set(base, value);
    }
  }

  // The legacy key is not guaranteed to be enumerated first, so hoist it.
  order.sort((a, b) => {
    if (a === LEGACY_SESSION_STORAGE_KEY) return -1;
    if (b === LEGACY_SESSION_STORAGE_KEY) return 1;
    return 0;
  });

  const out: Array<{ key: string; raw: string }> = [];
  for (const base of order) {
    const direct = whole.get(base);
    if (direct !== undefined && direct !== '') {
      out.push({ key: base, raw: direct });
      continue;
    }
    const parts = chunks.get(base);
    if (!parts) continue;
    let joined = '';
    for (let i = 0; parts.has(i); i++) joined += parts.get(i)!;
    if (joined) out.push({ key: base, raw: joined });
  }
  return out;
}

/**
 * Resolve the operator's session from storage, classified.
 *
 * A live token anywhere in storage wins: an expired leftover under one key must
 * not mask a good session under another. Only when nothing live is found do we
 * report WHY — expired beats unreadable beats signed_out, because that is the
 * order of usefulness to the person reading the message.
 */
export function readOperatorSession(
  storage: SessionStorageLike | null = defaultStorage(),
  now: number = Date.now(),
): OperatorSession {
  if (!storage) {
    return { state: 'signed_out', accessToken: null, expiresAt: null, storageKey: null };
  }

  let candidates: Array<{ key: string; raw: string }>;
  try {
    candidates = collectCandidates(storage);
  } catch {
    return { state: 'unreadable', accessToken: null, expiresAt: null, storageKey: null };
  }

  if (candidates.length === 0) {
    return { state: 'signed_out', accessToken: null, expiresAt: null, storageKey: null };
  }

  let expired: OperatorSession | null = null;
  let unreadable: OperatorSession | null = null;

  for (const { key, raw } of candidates) {
    const envelope = parseStoredSession(raw);
    const session = envelope ? extractSession(envelope) : null;
    if (!session) {
      unreadable ??= { state: 'unreadable', accessToken: null, expiresAt: null, storageKey: key };
      continue;
    }
    if (isExpired(session.expiresAt, now)) {
      expired ??= { state: 'expired', accessToken: null, expiresAt: session.expiresAt, storageKey: key };
      continue;
    }
    return {
      state: 'authenticated',
      accessToken: session.accessToken,
      expiresAt: session.expiresAt,
      storageKey: key,
    };
  }

  return expired ?? unreadable ?? {
    state: 'signed_out', accessToken: null, expiresAt: null, storageKey: null,
  };
}

/* ── Turning a session into request headers ─────────────────────────── */

export type OperatorAuthHeadersResult =
  | { ok: true; headers: Record<string, string>; session: OperatorSession }
  | { ok: false; failure: OperatorAuthFailure; message: string; session: OperatorSession | null };

/**
 * Build the headers for an operator-authenticated Supabase request, or explain
 * why we cannot. There is deliberately no third outcome: a caller that gets
 * `ok: false` has no headers to fall back to and must surface `message`.
 *
 * `apikey` carries the anon key because it identifies the PROJECT. Authorization
 * carries the operator's own JWT and nothing else, ever.
 */
export function operatorAuthHeaders(
  anonKey: string,
  opts: { session?: OperatorSession; storage?: SessionStorageLike | null; now?: number } = {},
): OperatorAuthHeadersResult {
  if (!anonKey) {
    return {
      ok: false,
      failure: 'missing_project_key',
      message: operatorAuthMessage('missing_project_key'),
      session: null,
    };
  }
  const session = opts.session
    ?? readOperatorSession(opts.storage === undefined ? defaultStorage() : opts.storage, opts.now);
  if (session.state !== 'authenticated' || !session.accessToken) {
    return {
      ok: false,
      failure: session.state === 'authenticated' ? 'unreadable' : session.state,
      message: operatorAuthMessage(session.state === 'authenticated' ? 'unreadable' : session.state),
      session,
    };
  }
  return {
    ok: true,
    session,
    headers: {
      'Content-Type': 'application/json',
      apikey: anonKey,
      Authorization: `Bearer ${session.accessToken}`,
    },
  };
}

/** The operator-facing sentence for each failure. Plain hyphens only, no
 *  em-dashes (Rule 7), and each one names the state rather than describing the
 *  data, because the data is not what went wrong. */
export function operatorAuthMessage(failure: OperatorAuthFailure): string {
  switch (failure) {
    case 'signed_out':
      return 'You are not signed in, so I cannot read anything live. Sign in to the operator console and ask me again.';
    case 'expired':
      return 'Your operator session has expired, so I cannot read anything live. Sign in again and ask me again.';
    case 'unreadable':
      return 'I could not read your operator session, so I cannot read anything live. Sign in again and ask me again.';
    case 'missing_project_key':
      return 'This console is missing its Supabase project key, so I cannot read anything live. That is a deployment problem, not a sign-in problem.';
  }
}

/* ── Preferring a refreshing reader over a storage snapshot ─────────── */

/**
 * A host-supplied token reader. Resolves to the operator's access token, `null`
 * when there is genuinely no session, or the literal `'expired'` when a session
 * exists but can no longer be renewed.
 *
 * That three-way return is `teglo-dashboard`'s `ORA.accessToken()`
 * (`components/canonical/ora-config.js`), which is the sanctioned reader on
 * every operator page and is strictly better than anything this module can do
 * alone: it goes through the page's single registered supabase-js client, so
 * `getSession()` transparently REFRESHES a session that has expired or is about
 * to. A storage snapshot cannot refresh; it can only classify. So where a
 * provider exists we defer to it, and `readOperatorSession` is the fallback for
 * surfaces that have no such client (ora-core's own web app, the dock's
 * standalone build, a blocked or offline supabase-js).
 */
export type OperatorTokenProvider =
  () => string | null | Promise<string | null>;

/** The sentinel a provider returns for "signed in once, cannot be renewed". */
export const EXPIRED_SESSION_SENTINEL = 'expired';

/**
 * Async twin of `operatorAuthHeaders` that prefers a refreshing provider.
 *
 * A provider's answer is authoritative: it has already consulted storage, so a
 * `null` from it means signed out and we do NOT go behind it with a second read
 * that might resurrect a stale snapshot. Only a provider that THROWS falls back
 * to classifying storage directly, because a thrown provider answered nothing
 * at all.
 */
export async function resolveOperatorAuthHeaders(
  anonKey: string,
  opts: {
    provider?: OperatorTokenProvider | null;
    storage?: SessionStorageLike | null;
    now?: number;
  } = {},
): Promise<OperatorAuthHeadersResult> {
  if (!anonKey) {
    return {
      ok: false,
      failure: 'missing_project_key',
      message: operatorAuthMessage('missing_project_key'),
      session: null,
    };
  }
  if (opts.provider) {
    try {
      const token = await opts.provider();
      if (token === EXPIRED_SESSION_SENTINEL) {
        return {
          ok: false,
          failure: 'expired',
          message: operatorAuthMessage('expired'),
          session: { state: 'expired', accessToken: null, expiresAt: null, storageKey: null },
        };
      }
      if (typeof token === 'string' && token) {
        return operatorAuthHeaders(anonKey, {
          session: { state: 'authenticated', accessToken: token, expiresAt: null, storageKey: null },
        });
      }
      return {
        ok: false,
        failure: 'signed_out',
        message: operatorAuthMessage('signed_out'),
        session: { state: 'signed_out', accessToken: null, expiresAt: null, storageKey: null },
      };
    } catch {
      // The provider answered nothing, so classify storage ourselves rather
      // than report a state we did not actually establish.
    }
  }
  return operatorAuthHeaders(anonKey, { storage: opts.storage, now: opts.now });
}

/**
 * A 401/403 from the server means the token we sent was refused, whatever
 * storage claimed about its expiry. Callers route those statuses back through
 * the same vocabulary instead of reporting them as a count or an empty list.
 */
export function isAuthFailureStatus(status: number): boolean {
  return status === 401 || status === 403;
}

/** The message for a server-side rejection of a token we believed was live.
 *  Storage said signed in; the server disagreed, so the session is stale. */
export function rejectedSessionMessage(): string {
  return operatorAuthMessage('expired');
}

/**
 * P0-1 (2026-08-18). NOT EVERY 403 IS A STALE SESSION, and treating them alike is what
 * made the "Resolve returns 403 for a real operator" report so hard to read.
 *
 * The gateway logs for the two refusals that day carry
 *   request.sb.jwt.authorization.payload.role = "authenticated"
 *   request.sb.auth_user = 0ec59c1b-ccf4-4057-af92-6256db77870c
 * — a live, freshly signed-in user token (that user had signed in two minutes earlier),
 * not anon and not expired. Its app_metadata.ora_role is 'client'. The server refused
 * correctly; the UI then said "your session has expired, sign in again", so the operator
 * signed out and back in — as the same client account — and nothing changed. The UI was
 * instructing them to do the one thing that could not work.
 *
 * The estate's operator functions already return the decoded role on a refusal:
 * outage-mass-resolve echoes `decoded.ora_role` (and buckets `reason`), and
 * resolve-requests v8 returns `ora_role` + a ready `message`. This reads either shape and
 * says which account is signed in. Only when the payload gives no role do we fall back to
 * the stale-session vocabulary, which is then the honest reading.
 */
export function authRefusalMessage(payload: unknown, signedInEmail?: string | null): string {
  const p = (payload ?? {}) as Record<string, any>;
  const role: unknown = p.ora_role ?? p.decoded?.ora_role ?? null;
  if (typeof role === 'string' && role && role !== 'operator') {
    const who = signedInEmail || p.decoded?.email || p.email || null;
    return `You are signed in${who ? ` as ${who}` : ''}, which is a ${role} account. ` +
      'This action places a real outbound call and is limited to operator sign-ins — ' +
      'sign in with your operator account to do it.';
  }
  // An authenticated subject with no role at all: signed in, but not entitled. Still not expiry.
  const sub: unknown = p.sub ?? p.decoded?.sub ?? null;
  if (p.reason === 'role_claim_not_operator' || (sub && role === null && p.error === 'forbidden')) {
    return 'You are signed in, but this sign-in carries no operator role, so it cannot place ' +
      'outbound calls. Sign in with your operator account.';
  }
  return rejectedSessionMessage();
}
